import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import ReactPlayer from "react-player";
import type { OnProgressProps } from "react-player/base";

import type { RootState } from "../../../store/state/rootReducer";
import AudioFileWave from "../../../assets/svg/AudioFileWave.svg";

import ClippingBar from "./ClippingBar/TSClippingBar";
import Controls from "./Controls/TSControls";
import CurrentTimeRef from "./ClippingBar/TSCurrentTimeRef";
import { formatTime } from "./utils";

interface TSVideoClipperProps {
  videoSrc: string;
  startingSecond: number;
  endingSecond: number;
  videoDuration: number;
  setClipStartToSave: (second: any) => void;
  setClipEndToSave: (second: any) => void;
}

const TSVideoClipper = ({
  videoSrc,
  startingSecond,
  endingSecond,
  videoDuration,
  setClipStartToSave,
  setClipEndToSave,
}: TSVideoClipperProps) => {
  const videoRef = useRef<ReactPlayer>(null);
  const durationRef = useRef(videoDuration);

  const { story } = useSelector((s: RootState) => s.storyAuthoring);
  const isAudioFile = story.custom_archive_media_type && story.custom_archive_media_type.startsWith("audio");

  // Controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const [playbackRate, setPlaybackRate] = useState(1.0);

  // Video Progress
  const [played, setPlayed] = useState<number>(startingSecond);
  const [clipRange, setClipRange] = useState<number[]>([startingSecond, endingSecond]);
  const handlePlayPause = () => setIsPlaying((prev) => !prev);
  const handleMute = useCallback(() => setIsMuted((prev) => !prev), []);
  const handlePlaybackRate = useCallback((rate: number) => setPlaybackRate(rate), []);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const duration = durationRef.current;
  const padding = 5;
  // left is 0 or padding seconds before initial clip
  const left = startingSecond > padding ? startingSecond - padding : 0;
  // right is duration or padding seconds past initial clip
  const right = endingSecond + padding < duration ? endingSecond + padding : duration;
  const clipRangeLength = right - left;
  const playedWithinClipRange = played - left;
  const calculatedPlayedValue = (playedWithinClipRange / clipRangeLength) * 100;

  const handleVolume = useCallback((event: Event, value: number | number[]) => {
    if (typeof value === "number") {
      setVolume(value / 100);
      setIsMuted(value === 0);
    }
  }, []);

  const handleVolumeSeekDown = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    if (typeof value === "number") {
      setVolume(value / 100);
      setIsMuted(value === 0);
    }
  };

  const handleForward = () => {
    if (videoRef?.current) {
      const timeCandidate = videoRef.current.getCurrentTime() + 5;
      const time = Math.min(videoRef.current.getDuration(), timeCandidate);
      videoRef?.current?.seekTo(time, "seconds");
    }
  };

  const handleReplay = () => {
    if (videoRef?.current) {
      const timeCandidate = videoRef.current.getCurrentTime() - 5;
      const time = timeCandidate > 0 ? timeCandidate : 0;
      videoRef?.current?.seekTo(time, "seconds");
    }
  };

  // Video Progress Events
  const handleProgress = (state: OnProgressProps) => {
    if (isPlaying && state.playedSeconds >= clipRange[1]) {
      setIsPlaying(false);
    }
    // had a to use a isFirstRender flag here because React Player
    // tries to start the video at the beginning of the video on rendering
    if (!isPlaying && isFirstRender) {
      setPlayed(startingSecond);
      videoRef.current?.seekTo(startingSecond);
      setIsFirstRender(false);
    } else {
      setPlayed(state.playedSeconds);
    }
  };

  const handleProgressRefSeek = (event: Event, value: number | number[]) => {
    if (typeof value === "number") {
      const updatedPlayedWithinClipRange = (value / 100) * clipRangeLength;
      const playedScoped = left + updatedPlayedWithinClipRange;

      setPlayed(playedScoped);
      videoRef.current?.seekTo(playedScoped / duration, "fraction");
    }
  };

  // Clipping Bar Events
  const handleClipRange = (event: Event, value: number | number[]) => {
    console.log("value handleCLipRange", value);
    const isMin = (valueScoped: number, min: number) => valueScoped === min;
    const isMax = (valueScoped: number, max: number) => valueScoped === max;

    if (isMin(value as number, left)) {
      // if the value is equal to the minimum, set the start of the clip to the minimum
      setClipRange([left, clipRange[1]]);
    } else if (isMax(value as number, right)) {
      // if the value is equal to the maximum, set the end of the clip to the maximum
      setClipRange([clipRange[0], right]);
    } else {
      // otherwise, update the clip range
      setClipRange(value as number[]);
    }
    // We update the video to the start of the clip range every time the user adjusts the clip range
    if (value[0] !== clipRange[0]) {
      setClipStartToSave(clipRange[0]);
    }
    if (value[1] !== clipRange[1]) {
      setClipEndToSave(clipRange[1]);
    }
  };

  useEffect(() => {
    // We update the video to the start of the clip range every time the user adjusts the clip range
    videoRef.current?.seekTo(clipRange[0] / duration, "fraction");
  }, [clipRange[0]]);

  useEffect(() => {
    if (clipRange[0] !== startingSecond || clipRange[1] !== endingSecond) {
      setClipRange([startingSecond, endingSecond]);
    }
  }, [startingSecond, endingSecond]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "5px",
        overflow: "hidden",
        margin: "0 auto",
        height: 0,
        paddingBottom: "56.25%",
        background: isAudioFile ? "#D9DCDE" : "black",
      }}
    >
      <Box sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}>
        <ReactPlayer
          style={{ borderRadius: "5px", overflow: "hidden" }}
          width="100%"
          height="100%"
          ref={videoRef}
          url={videoSrc}
          playing={isPlaying}
          muted={isMuted}
          playbackRate={playbackRate}
          progressInterval={200}
          onProgress={handleProgress}
          volume={volume}
          controls={false}
          playsinline
        />
      </Box>
      {isAudioFile && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: "72px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={AudioFileWave} style={{ width: "119px", height: "56px" }} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "absolute",
          bottom: 0,
          right: 0,
          left: 0,
          width: "100%",
          background: isAudioFile
            ? "rgba(142, 151, 159, 0.9)"
            : "linear-gradient(360deg, #000000 42.19%, rgba(0, 0, 0, 0) 100%)",
          zIndex: 2,
          color: "white",
        }}
      >
        <Grid container alignContent="center" sx={{ width: "100%" }}>
          <Grid
            item
            xs={12}
            sx={{
              padding: "0px",
              position: "relative",
            }}
          >
            <ClippingBar
              min={left}
              clipRange={clipRange}
              totalDuration={right}
              formattedStartTime={formatTime(clipRange[0])}
              formattedEndTime={formatTime(clipRange[1])}
              handleClipRange={handleClipRange}
              isAudioFile={isAudioFile}
            />
            <CurrentTimeRef value={calculatedPlayedValue} handleProgressRefSeek={handleProgressRefSeek} />
          </Grid>
        </Grid>
        <Controls
          isPlaying={isPlaying}
          isMuted={isMuted}
          volume={volume}
          playbackRate={playbackRate}
          formattedCurrentTime={formatTime(played)}
          formattedTotalDuration={formatTime(right)}
          handlePlayPause={handlePlayPause}
          handleReplay={handleReplay}
          handleForward={handleForward}
          handleMute={handleMute}
          handlePlaybackRate={handlePlaybackRate}
          handleVolume={handleVolume}
          handleVolumeSeekDown={handleVolumeSeekDown}
        />
      </Box>
    </Box>
  );
};

export default TSVideoClipper;
